import type { ComponentProps } from "react";
import { tv } from "tailwind-variants";

import { Label, Message } from "@/ui";
import type { Option } from "@/utils";

const selectVariants = tv({
  slots: {
    container: "relative",
    select: [
      "block w-full cursor-pointer appearance-none rounded-lg border bg-surface py-2 pr-9 pl-3",
      "border-line-control text-sm text-content shadow-raised",
      "transition-[color,box-shadow,border-color] duration-150",
      "hover:border-content-subtle",
      "focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-focus-ring/40 focus-visible:outline-none",
      "disabled:cursor-not-allowed disabled:border-line-strong disabled:bg-surface-sunken disabled:text-content-subtle",
    ],
    chevron: "pointer-events-none absolute inset-y-0 right-3 flex items-center text-content-subtle",
  },
  variants: {
    error: {
      true: {
        select: "border-danger focus-visible:border-danger focus-visible:ring-danger/30",
      },
    },
  },
});

const { chevron, container, select } = selectVariants();

type SelectProps = {
  id: string;
  label?: string;
  error?: string | boolean;
  options: Option[];
  emptyOptionLabel?: string;
  hideEmptyOption?: boolean;
  containerClassName?: string;
} & ComponentProps<"select">;

export const Select = ({
  className,
  containerClassName,
  emptyOptionLabel = "Select an option",
  error,
  hideEmptyOption = false,
  id,
  label,
  options,
  ...rest
}: SelectProps) => {
  const hasError = Boolean(error);
  const messageId = `${id}-message`;

  return (
    <div className={container({ className: containerClassName })}>
      {label ? (
        <Label disabled={rest.disabled} htmlFor={id} label={label} required={rest.required} />
      ) : null}

      <div className="relative flex items-center">
        <select
          id={id}
          {...rest}
          aria-describedby={hasError ? messageId : rest["aria-describedby"]}
          aria-invalid={hasError || undefined}
          className={select({ className, error: hasError })}
        >
          {hideEmptyOption ? null : <option value="">{emptyOptionLabel}</option>}

          {options.map(({ label, value }) => {
            return (
              <option key={value} value={value}>
                {label}
              </option>
            );
          })}
        </select>

        <span aria-hidden="true" className={chevron()}>
          <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 20 20">
            <path
              d="m6 8 4 4 4-4"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.75"
            />
          </svg>
        </span>
      </div>

      {hasError ? <Message error={error} id={messageId} /> : null}
    </div>
  );
};
